import { useState, useEffect } from "react";
import { Grid, Typography, Box, Button } from "@mui/material";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import styles from "./style.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { api, resolveUrl } from "../../../config/apis";
import StudentApi from "../../../config/apis/student";
import PostAssessmentForm from "./edit";
import Moment from "react-moment";

// Field sequence mapping based on the form order in edit.jsx
const FIELD_SEQUENCE = [
  // Basic Information Section
  'student_id',
  'cohort', 
  'section',
  'registration_date',
  'shift_days',
  'shift_time',
  
  // Household Items Section
  'household_items',
  'smartphone',
  'laptopdesktop',
  
  // Employment Status Section
  'currently_employed',
  'position',
  'salary',
  'employer_name',
  
  // Training Related Work Section
  'code_girls_training_related_work',
  'code_girls_training_related_work_other',
  
  // Partner Opportunity Section
  'interested_in_partner_opportunity',
  'interested_in_partner_opportunity_other',
  'opportunity_preference',
  
  // Advanced Phase Section
  'interested_in_advanced_phase',
  'interested_in_advanced_phase_other',
  
  // Post-Assessment Specific Fields
  'recommend_consulnet',
  'interested_in_events',
  'interested_in_events_other',
  'prefer_freelancing',
  'prefer_freelancing_other',
  'training_objective_achieved',
  'training_impact_personal_life',
  'post_training_activity',
  'post_training_activity_other',
  'future_plans',
  'employment_status',
  'unemployment_reasons',
  'number_of_jobs',
  'employment_relevance',
  'total_experience_years',
  'tech_experience_years',
  'career_without_training',
  'pay_better_than_before',
  'pay_better_reason',
  'freelancing_platforms',
  'freelancing_projects',
  'freelancing_income',
  'freelancing_technologies',
  'freelancing_help_from_training',
  'business_name',
  'business_nature',
  'business_sales_platforms',
  'business_tech_enabled',
  'business_duration',
  'business_monthly_revenue',
  'engagement_feature_journey',
  'engagement_community_roles'
];

export default function PostAssessmentFormView() {
  const params = useParams();

  const nevigate = useNavigate();

  const [formData, setFormData] = useState();
  const [formSuccess, setFormSuccess] = useState(false);
  const onUpdate = async () => {
    nevigate(`/admin/post-assessment-form`);
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(
          resolveUrl(StudentApi.GET_POST_ASSESSMENT_FORM_DATA, params.id ?? "")
        );
        setFormData(res.data.data);
      } catch (error) {
        console.error("Error fetching general data:", error);
      }
    };

    fetchData();
  }, [formSuccess]);

  const renderFormData = () => {
    if (!formData) {
      return "There are no records to display";
    }

    // Filter out empty/null/undefined values and sort by FIELD_SEQUENCE
    const validFields = FIELD_SEQUENCE.filter(key => {
      const value = formData[key];
      return value !== "" && 
             value !== null && 
             value !== undefined && 
             !(Array.isArray(value) && value.length === 0);
    });

    return validFields.map((key) => {
      const value = formData[key];
      // Remove underscores from the key
      const formattedKey = key.replace(/_/g, " ");
      
      // Special display names for certain fields
      const displayName = {
        "laptopdesktop": "Laptop Desktop",
        "training_objective_achieved": "Did you achieve your objectives by joining the training?",
        "recommend_consulnet": "Do you recommend ConsulNet Corporation to other people?",
        "training_impact_personal_life": "What was the impact of training on your personal life?",
        "post_training_activity": "What did you do after graduating from the training?",
        "post_training_activity_other": "Other activity details",
        "future_plans": "What do you plan to do in the future?",
        "employment_status": "Have you ever been employed/freelanced/run a business?",
        "unemployment_reasons": "Reason(s) for not working",
        "number_of_jobs": "Number of jobs since completing the training",
        "employment_relevance": "Relevance of employment to skills learnt at ConsulNet",
        "total_experience_years": "Total years of professional experience",
        "tech_experience_years": "Years of experience in tech industry",
        "career_without_training": "I would have had a career without the training too.",
        "pay_better_than_before": "Is your current pay better than before you got the training?",
        "pay_better_reason": "Do you think it is because of the training?",
        "freelancing_platforms": "Platforms used",
        "freelancing_projects": "Share links of Projects completed",
        "freelancing_income": "Average monthly income",
        "freelancing_technologies": "Technologies used",
        "freelancing_help_from_training": "Did your training at ConsulNet help in freelancing journey?",
        "business_name": "Business Name",
        "business_nature": "Nature of business",
        "business_sales_platforms": "Sales platforms used",
        "business_tech_enabled": "Tech-enabled?",
        "business_duration": "Duration (years)",
        "business_monthly_revenue": "Monthly revenue",
        "engagement_feature_journey": "Would you like your journey to be featured?",
        "engagement_community_roles": "Would you like to engage with the CodeGirls community as:"
      }[key] || formattedKey;

      return (
        <Grid key={key} item xl={4} lg={6} xs={12} className={styles.grid_col}>
          <Box className={styles.box}>
            <Typography
              variant="body1"
              className={`${styles.label_key} ${key}`}
            >
              {displayName}
            </Typography>
            {Array.isArray(value) ? (
              <ul>
                {value.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <Typography variant="body1" fontWeight={700}>
                {key === "registration_date" ? (
                  <Moment format="DD/MM/YYYY ">{value}</Moment>
                ) : (
                  value
                )}
              </Typography>
            )}
          </Box>
        </Grid>
      );
    });
  };

  return (
    <>
      {params && !params.id && !formData ? (
        <PostAssessmentForm setFormSuccess={setFormSuccess} />
      ) : (
        <Box
          className={`container global-user ${styles.preAssessmentFormContainer}  ${styles.PreAssessmentFormView}`}
        >
          <Typography variant="h1" className="main-heading">
            Post Assessment
          </Typography>
          <Grid container className={`page-container ${styles.addStudent}`}>
            <Grid container spacing={2}>
              {renderFormData()}
            </Grid>
            <Grid container justifyContent={"flex-end"} marginTop={5}>
              {params && !params.id && (
                <Button
                  className={styles.btn}
                  type="submit"
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  onClick={() => onUpdate()}
                >
                  Update
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}